import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Server, Plus, Clock, AlertCircle } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  Button,
  Card,
  StatusCell,
  MoneyCell,
  DateCell,
  ActionsMenuCell,
  CpanelButton,
  useToast,
  type StatusTone,
} from '@sighthost/ui';
import { useServices, serviceService, type Service, type ServiceStatus } from '@/domains/hosting';
import { supabase } from '@/shared/lib/supabase';

interface ServiceOrderRow {
  id: string;
  primary_fqdn: string | null;
  fqdn_mode: 'register' | 'existing' | 'later';
  status: string;
  created_at: string;
  invoice_id: string | null;
  invoice: { number: string; total: number; status: string } | null;
}

const PENDING_STATUS_LABEL: Record<string, { label: string; tone: 'warning' | 'pending' | 'danger' }> = {
  pending_payment:     { label: 'Awaiting payment',                                         tone: 'warning' },
  paid:                { label: 'Payment received — preparing your account',                tone: 'pending' },
  provisioning:        { label: 'Setting up your cPanel account — usually 1–2 min',         tone: 'pending' },
  provisioning_domain: { label: 'Account ready — registering your domain',                  tone: 'pending' },
  failed_retryable:    { label: 'Hit a temporary issue — retrying automatically',           tone: 'warning' },
  failed_terminal:     { label: 'Setup failed — our team has been notified',                tone: 'danger'  },
};

const CPANEL_ERROR_COPY: Record<string, string> = {
  email_not_verified: 'Verify your email address to access cPanel.',
  forbidden_not_owner: "You don't have access to this hosting account.",
  rate_limited: 'Too many cPanel sessions in the past hour — please wait a few minutes.',
  whm_session_failed: 'cPanel server is unreachable right now. Please try again.',
  whm_status_check_failed: 'Could not check service status with cPanel. Try again shortly.',
};

const STATUS_LABEL: Record<ServiceStatus, string> = {
  active: 'Active',
  pending: 'Pending',
  suspended: 'Suspended',
  cancelled: 'Cancelled',
  terminated: 'Terminated',
  fraud: 'Under review',
};

const STATUS_TONE: Record<ServiceStatus, StatusTone> = {
  active: 'active',
  pending: 'pending',
  suspended: 'failed',
  cancelled: 'draft',
  terminated: 'draft',
  fraud: 'overdue',
};

const CYCLE_LABEL: Record<Service['billingCycle'], string> = {
  monthly: '/ month',
  quarterly: '/ quarter',
  'semi-annually': '/ 6 months',
  annually: '/ year',
  biennially: '/ 2 years',
  triennially: '/ 3 years',
};

export default function ServicesListPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useServices();
  const { toast } = useToast();
  const [pendingOrders, setPendingOrders] = useState<ServiceOrderRow[]>([]);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      // Show in-flight orders only — completed/cancelled live in the active services list.
      const { data: rows } = await supabase
        .from('service_order')
        .select('id, primary_fqdn, fqdn_mode, status, created_at, invoice_id, invoice:invoice_id(number, total, status)')
        .not('status', 'in', '(complete,cancelled)')
        .order('created_at', { ascending: false });
      if (cancelled) return;
      // Supabase nests the joined row under invoice; coerce shape.
      setPendingOrders(((rows ?? []) as unknown as ServiceOrderRow[]));
    }
    void load();
    const interval = window.setInterval(() => void load(), 30_000);
    return () => { cancelled = true; window.clearInterval(interval); };
  }, []);

  async function cancelOrder(orderId: string) {
    if (cancellingId) return;
    const ok = window.confirm('Cancel this pending order? The invoice will be voided.');
    if (!ok) return;
    setCancellingId(orderId);
    try {
      const { error: cancelErr } = await supabase
        .from('service_order')
        .update({ status: 'cancelled' })
        .eq('id', orderId);
      if (cancelErr) {
        toast({ tone: 'danger', title: 'Could not cancel order', body: cancelErr.message });
        return;
      }
      setPendingOrders((prev) => prev.filter((o) => o.id !== orderId));
      toast({ tone: 'success', title: 'Order cancelled' });
    } finally {
      setCancellingId(null);
    }
  }

  const columns: UniversalTableColumn<Service>[] = [
    {
      key: 'primaryDomain',
      header: 'Service',
      sortValue: (s) => s.primaryDomain,
      render: (s) => (
        <div className="flex flex-col">
          <span className="inline-flex items-center gap-2 font-medium text-ink">
            <Server className="h-4 w-4 text-muted-foreground" />
            {s.primaryDomain}
          </span>
          <span className="text-xs text-muted-foreground">{s.plan.name}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (s) => s.status,
      render: (s) => (
        <StatusCell tone={STATUS_TONE[s.status]} label={STATUS_LABEL[s.status]} />
      ),
      width: 'w-32',
    },
    {
      key: 'nextDueDate',
      header: 'Next due',
      sortValue: (s) => s.nextDueDate,
      render: (s) => <DateCell value={s.nextDueDate} format="long" />,
      hideOnMobile: true,
    },
    {
      key: 'amount',
      header: 'Recurring',
      sortValue: (s) => s.recurringAmountZAR,
      render: (s) => (
        <div className="flex flex-col items-end">
          <MoneyCell value={s.recurringAmountZAR} />
          <span className="text-xs text-muted-foreground">{CYCLE_LABEL[s.billingCycle]}</span>
        </div>
      ),
      align: 'right',
      width: 'w-36',
    },
    {
      key: 'cpanel',
      header: '',
      render: (s) => (
        <div onClick={(e) => e.stopPropagation()} className="flex justify-end">
          <CpanelButton
            service={{ cpanel_username: s.cpanelUsername, status: s.status }}
            onIssueSession={(cpanel_username) => serviceService.createCpanelSession({ cpanel_username })}
            onError={(err) => {
              toast({
                tone: 'warning',
                title: 'Could not open cPanel',
                body: CPANEL_ERROR_COPY[err.code] ?? err.message,
              });
            }}
            variant="outline"
            size="sm"
          />
        </div>
      ),
      width: 'w-44',
      align: 'right',
    },
    {
      key: 'actions',
      header: '',
      render: (s) => (
        <div onClick={(e) => e.stopPropagation()} className="flex justify-end">
          <ActionsMenuCell
            ariaLabel={`Actions for ${s.primaryDomain}`}
            items={[
              { label: 'Open service', onSelect: () => navigate(`/app/services/${s.id}`) },
              { label: 'Billing', onSelect: () => navigate(`/app/services/${s.id}/billing`) },
              s.status === 'active' && {
                label: 'Cancel service',
                tone: 'danger' as const,
                onSelect: () => navigate(`/app/services/${s.id}/manage?cancel=1`),
                separated: true,
              },
            ].filter(Boolean) as React.ComponentProps<typeof ActionsMenuCell>['items']}
          />
        </div>
      ),
      width: 'w-12',
      align: 'right',
    },
  ];

  return (
    <UniversalPage
      title="Services"
      description="Hosting subscriptions and addons."
      breadcrumbs={[{ label: 'Home', href: '/app' }, { label: 'Services' }]}
      actions={
        <Button size="sm" onClick={() => navigate('/app/services/new')}>
          <Plus className="h-4 w-4" />
          Order new
        </Button>
      }
    >
      {pendingOrders.length > 0 && (
        <Card interactive={false} className="mb-6">
          <header className="flex items-center justify-between border-b border-border px-6 py-3">
            <h2 className="text-sm font-semibold text-ink">In-flight orders</h2>
            <span className="text-xs text-muted-foreground">{pendingOrders.length} pending</span>
          </header>
          <ul className="divide-y divide-border">
            {pendingOrders.map((o) => {
              const statusInfo = PENDING_STATUS_LABEL[o.status] ?? { label: o.status, tone: 'pending' as const };
              const StatusIcon = statusInfo.tone === 'danger' ? AlertCircle : Clock;
              return (
                <li key={o.id} className="flex items-center gap-3 px-6 py-3">
                  <Server className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-ink">
                      {o.primary_fqdn ?? <span className="italic text-muted-foreground">Temporary URL (decide later)</span>}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <StatusIcon className="size-3" aria-hidden /> {statusInfo.label}
                      {o.invoice && (
                        <>
                          <span>·</span>
                          <span>{o.invoice.number}</span>
                          <MoneyCell value={Number(o.invoice.total)} className="text-xs" />
                        </>
                      )}
                    </div>
                  </div>
                  {o.status === 'pending_payment' && o.invoice_id && (
                    <>
                      <Button size="sm" onClick={() => navigate(`/app/billing/invoices/${o.invoice_id}`)}>
                        Pay invoice
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => cancelOrder(o.id)}
                        loading={cancellingId === o.id}
                        disabled={cancellingId !== null}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  {o.status !== 'pending_payment' && (
                    <Link
                      to={`/app/services/orders/${o.id}`}
                      className="text-xs font-medium text-primary-ink hover:underline"
                    >
                      Track →
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {/*
        NO FILTER BAR HERE, deliberately (2026-09-13).

        This is one customer's own services — typically one to three rows, and
        the page already shows all of them at once. A search box over three
        items is noise, and a period filter on a stock set that small is the
        inert control F-3 forbids: it implies a list long enough to need
        narrowing, which this is not.

        Revisit if a single customer routinely holds more than a screenful.
        The admin-side equivalent (AdminAccountsPage) IS filtered, because it
        spans every customer.
      */}
      <UniversalTable
        data={data}
        columns={columns}
        getRowKey={(s) => s.id}
        onRowClick={(s) => navigate(`/app/services/${s.id}`)}
        isLoading={isLoading}
        error={error}
        empty={{
          icon: Server,
          title: 'No services yet',
          description: 'Order a hosting plan to get started.',
          action: (
            <Button size="sm" onClick={() => navigate('/app/services/new')}>
              <Plus className="h-4 w-4" />
              Order new
            </Button>
          ),
        }}
        caption={
          !isLoading && !error
            ? `${data.length} service${data.length === 1 ? '' : 's'}`
            : undefined
        }
      />
    </UniversalPage>
  );
}
